import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  TablePagination,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
} from "@mui/material";
import {
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Speed as SpeedIcon,
  BugReport as DamageIcon,
  Clear as ClearIcon,
  ViewList as ViewListIcon,
  CalendarMonth as CalendarMonthIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Close as CloseIcon,
  Handyman as HandymanIcon,
} from "@mui/icons-material";
import axios from "axios";
import { API_URL } from "../utils/api-config";
import { Calendar, momentLocalizer } from "react-big-calendar";
import type { Event } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./VehiclesHistory.css";
import "moment/dist/locale/fr";

// Configuration de moment en français
moment.locale("fr");
const localizer = momentLocalizer(moment);

interface Vehicle {
  _id: string;
  plate: string;
  brand: string;
  model: string;
  year: string;
  mileage: string;
  status: string;
  assignmentCount?: number;
  lastAssignmentDate?: string;
  lastDriverId?: string;
  lastDriverName?: string;
  totalKilometers?: number;
  totalDamages?: number;
  hasAssignment?: boolean;
}

type VehicleSortField =
  | "plate"
  | "assignmentCount"
  | "lastAssignmentDate"
  | "totalKilometers"
  | "totalDamages";

interface Damage {
  position: {
    x: number;
    y: number;
  };
  type: string;
  face: string;
  driverId: string;
  driverName?: string;
  description?: string;
  images?: string[];
  createdAt: string;
}

interface Assignment {
  _id: string;
  vehiclePlate: string;
  driverId: string;
  driverFirstName: string;
  driverLastName: string;
  date: string;
  time: string;
  status: string;
  startMileage?: number;
  endMileage?: number;
  damages?: Damage[];
}

interface CalendarEvent extends Event {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: {
    vehiclePlate: string;
    vehicleBrand?: string;
    vehicleModel?: string;
    driverId: string;
    driverName: string;
    status: string;
    assignmentId: string;
    startMileage?: number;
    endMileage?: number;
    damageCount?: number;
  };
}

const VehiclesHistoryPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canAccessDashboard = hasPermission("dashboard", "read");
  
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [totalAssignments, setTotalAssignments] = useState(0);
  const [totalKilometers, setTotalKilometers] = useState(0);
  const [totalDamages, setTotalDamages] = useState(0);
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [filterBrand, setFilterBrand] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortBy, setSortBy] = useState<VehicleSortField>("lastAssignmentDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<
    "month" | "week" | "work_week" | "day" | "agenda"
  >("month");

  // Récupération des données
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Récupérer toutes les affectations et véhicules en parallèle
        const [assignmentsResponse, vehiclesResponse] = await Promise.all([
          axios.get(`${API_URL}/assignments`),
          axios.get(`${API_URL}/vehicles`),
        ]);

        const assignments = assignmentsResponse.data;
        const allVehicles = vehiclesResponse.data;

        // Construire un ensemble des plaques uniques et calculer les stats
        const uniquePlates = new Set<string>();
        const assignmentsByPlate: Record<string, number> = {};
        const lastAssignmentByPlate: Record<string, string> = {};
        const lastDriverByPlate: Record<string, { id: string; name: string }> =
          {};
        const statsByPlate: Record<string, { km: number; damages: number }> =
          {};

        assignments.forEach((assignment: Assignment) => {
          uniquePlates.add(assignment.vehiclePlate);

          // Compter les affectations par plaque
          if (!assignmentsByPlate[assignment.vehiclePlate]) {
            assignmentsByPlate[assignment.vehiclePlate] = 0;
          }
          assignmentsByPlate[assignment.vehiclePlate]++;

          // Garder la date la plus récente et le dernier chauffeur
          if (
            !lastAssignmentByPlate[assignment.vehiclePlate] ||
            new Date(assignment.date) >
              new Date(lastAssignmentByPlate[assignment.vehiclePlate])
          ) {
            lastAssignmentByPlate[assignment.vehiclePlate] = assignment.date;
            lastDriverByPlate[assignment.vehiclePlate] = {
              id: assignment.driverId,
              name: `${assignment.driverFirstName} ${assignment.driverLastName}`,
            };
          }

          // Calculer km et dégâts directement depuis les affectations
          if (!statsByPlate[assignment.vehiclePlate]) {
            statsByPlate[assignment.vehiclePlate] = { km: 0, damages: 0 };
          }

          if (assignment.endMileage && assignment.startMileage) {
            const km = assignment.endMileage - assignment.startMileage;
            if (km > 0) statsByPlate[assignment.vehiclePlate].km += km;
          }

          if (assignment.damages && Array.isArray(assignment.damages)) {
            statsByPlate[assignment.vehiclePlate].damages +=
              assignment.damages.length;
          }
        });

        setTotalAssignments(assignments.length);

        // 2. Filtrer et enrichir les véhicules (sans appels API supplémentaires)
        const vehiclesData: Vehicle[] = [];
        let totalKm = 0;
        let totalDmg = 0;

        allVehicles.forEach((vehicle: Vehicle) => {
          if (uniquePlates.has(vehicle.plate)) {
            // Ajouter les informations calculées
            vehicle.assignmentCount = assignmentsByPlate[vehicle.plate] || 0;
            vehicle.lastAssignmentDate =
              lastAssignmentByPlate[vehicle.plate] || "";

            // Ajouter le dernier chauffeur
            if (lastDriverByPlate[vehicle.plate]) {
              vehicle.lastDriverId = lastDriverByPlate[vehicle.plate].id;
              vehicle.lastDriverName = lastDriverByPlate[vehicle.plate].name;
            }

            // Ajouter les statistiques
            const stats = statsByPlate[vehicle.plate] || { km: 0, damages: 0 };
            vehicle.totalKilometers = stats.km;
            vehicle.totalDamages = stats.damages;

            totalKm += stats.km;
            totalDmg += stats.damages;

            vehiclesData.push(vehicle);
          }
        });

        setVehicles(vehiclesData);
        setTotalKilometers(totalKm);
        setTotalDamages(totalDmg);

        // Créer les événements pour le calendrier
        const events: CalendarEvent[] = assignments.map(
          (assign: Assignment) => {
            const startDate = new Date(assign.date);
            const [hours, minutes] = assign.time.split(":").map(Number);
            startDate.setHours(hours, minutes, 0, 0);

            // Fin estimée : 8 heures plus tard
            const endDate = new Date(startDate);
            endDate.setHours(startDate.getHours() + 8);

            // Trouver le véhicule correspondant pour obtenir plus d'infos
            const vehicle = vehiclesData.find(
              (v) => v.plate === assign.vehiclePlate
            );

            return {
              id: assign._id,
              title: `${assign.vehiclePlate} - ${assign.driverFirstName} ${assign.driverLastName}`,
              start: startDate,
              end: endDate,
              resource: {
                vehiclePlate: assign.vehiclePlate,
                vehicleBrand: vehicle?.brand,
                vehicleModel: vehicle?.model,
                driverId: assign.driverId,
                driverName: `${assign.driverFirstName} ${assign.driverLastName}`,
                status: assign.status,
                assignmentId: assign._id,
                startMileage: assign.startMileage,
                endMileage: assign.endMileage,
                damageCount: assign.damages?.length || 0,
              },
            };
          }
        );

        setCalendarEvents(events);
      } catch (err) {
        console.error("Erreur lors de la récupération des données:", err);
        setError("Impossible de charger les données des véhicules.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Liste unique des marques pour le filtre
  const uniqueBrands = useMemo(() => {
    const brands = vehicles.map((v) => v.brand);
    return Array.from(new Set(brands)).sort();
  }, [vehicles]);

  // Liste unique des statuts pour le filtre
  const uniqueStatuses = useMemo(() => {
    const statuses = vehicles.map((v) => v.status);
    return Array.from(new Set(statuses)).sort();
  }, [vehicles]);

  // Filtrage et tri
  const filteredAndSortedVehicles = useMemo(() => {
    const filtered = vehicles.filter(
      (vehicle) =>
        (vehicle.plate.toLowerCase().includes(search.toLowerCase()) ||
          vehicle.brand.toLowerCase().includes(search.toLowerCase()) ||
          vehicle.model.toLowerCase().includes(search.toLowerCase())) &&
        (filterBrand === "" || vehicle.brand === filterBrand) &&
        (filterStatus === "" || vehicle.status === filterStatus)
    );

    filtered.sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case "plate":
          compareValue = a.plate.localeCompare(b.plate);
          break;
        case "assignmentCount":
          compareValue = (a.assignmentCount || 0) - (b.assignmentCount || 0);
          break;
        case "lastAssignmentDate":
          compareValue =
            new Date(a.lastAssignmentDate || 0).getTime() -
            new Date(b.lastAssignmentDate || 0).getTime();
          break;
        case "totalKilometers":
          compareValue = (a.totalKilometers || 0) - (b.totalKilometers || 0);
          break;
        case "totalDamages":
          compareValue = (a.totalDamages || 0) - (b.totalDamages || 0);
          break;
      }

      return sortOrder === "asc" ? compareValue : -compareValue;
    });

    return filtered;
  }, [vehicles, search, filterBrand, filterStatus, sortBy, sortOrder]);

  // Pagination
  const paginatedVehicles = filteredAndSortedVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          p: 3,
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Button
          onClick={() => navigate(canAccessDashboard ? "/vehicules" : "/")}
          variant="contained"
          startIcon={<ArrowBackIcon />}
        >
          {canAccessDashboard ? "Retour aux véhicules" : "Retour à l'accueil"}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)",
        py: { xs: 1, sm: 2, md: 4 },
        px: { xs: 1, sm: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          mx: "auto",
          boxShadow: { xs: 1, sm: 2, md: 3 },
          borderRadius: { xs: 2, sm: 3, md: 4 },
          background: "#fff",
          p: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 0.5, sm: 1, md: 3 },
          mb: { xs: 1, sm: 2, md: 4 },
          minHeight: { xs: "auto", md: "70vh" },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* En-tête */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            mb: { xs: 2, sm: 3 },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              width: { xs: "100%", sm: "auto" },
              flexWrap: "wrap",
            }}
          >
            {canAccessDashboard ? (
              <Button
                component={Link}
                to="/vehicules"
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                color="primary"
                size="small"
                sx={{
                  borderRadius: 2,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  Liste des véhicules
                </Box>
                <Box
                  component="span"
                  sx={{ display: { xs: "inline", sm: "none" } }}
                >
                  Retour
                </Box>
              </Button>
            ) : (
              <Button
                onClick={() => navigate("/")}
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                color="primary"
                size="small"
                sx={{
                  borderRadius: 2,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                Accueil
              </Button>
            )}
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <HistoryIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} />
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Historique des véhicules
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: "inline", sm: "none" } }}
              >
                Historique
              </Box>
            </Typography>
          </Box>

          {/* Barre de recherche */}
          <TextField
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ width: { xs: "100%", sm: 250, md: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearch("")}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Filtres, Tri et Toggle Vue */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            alignItems: "center",
          }}
        >
          <TextField
            select
            label="Marque"
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            size="small"
            sx={{
              minWidth: { xs: "45%", sm: 120, md: 150 },
              flex: { xs: "1 1 45%", sm: "0 0 auto" },
            }}
          >
            <MenuItem value="">Toutes</MenuItem>
            {uniqueBrands.map((brand) => (
              <MenuItem key={brand} value={brand}>
                {brand}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Statut"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            size="small"
            sx={{
              minWidth: { xs: "45%", sm: 120, md: 150 },
              flex: { xs: "1 1 45%", sm: "0 0 auto" },
            }}
          >
            <MenuItem value="">Tous</MenuItem>
            {uniqueStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Trier par"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as VehicleSortField)}
            size="small"
            sx={{
              minWidth: { xs: "70%", sm: 150, md: 200 },
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
            }}
          >
            <MenuItem value="lastAssignmentDate">Date</MenuItem>
            <MenuItem value="plate">Plaque</MenuItem>
            <MenuItem value="assignmentCount">Affectations</MenuItem>
            <MenuItem value="totalKilometers">Km</MenuItem>
            <MenuItem value="totalDamages">Dégâts</MenuItem>
          </TextField>

          <IconButton
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            color="primary"
            size="small"
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              display: { xs: "none", sm: "flex" },
            }}
          >
            {sortOrder === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
            sx={{
              width: { xs: "100%", sm: "auto" },
              "& .MuiToggleButton-root": {
                flex: { xs: 1, sm: "0 0 auto" },
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                px: { xs: 1, sm: 2 },
              },
            }}
          >
            <ToggleButton value="list">
              <ViewListIcon
                sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: 18, sm: 20 } }}
              />
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Liste
              </Box>
            </ToggleButton>
            <ToggleButton value="calendar">
              <CalendarMonthIcon
                sx={{ mr: { xs: 0, sm: 1 }, fontSize: { xs: 18, sm: 20 } }}
              />
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Calendrier
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Statistiques globales */}
        <Card
          sx={{
            mb: { xs: 2, sm: 3 },
            borderRadius: 2,
            boxShadow: 1,
            background: "linear-gradient(135deg, #f5f7ff 0%, #fafbff 100%)",
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 3 },
              "&:last-child": { pb: { xs: 2, sm: 3 } },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(4, 1fr)",
                },
                gap: { xs: 2, sm: 3 },
                textAlign: "center",
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                >
                  {vehicles.length}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                >
                  Véhicules
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="success.main"
                  sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                >
                  {totalAssignments}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                >
                  Affectations
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="secondary.main"
                  sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                >
                  {totalKilometers.toLocaleString()}
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    {" "}
                    km
                  </Box>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    Distance
                  </Box>
                  km
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="error.main"
                  sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                >
                  {totalDamages}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                >
                  Dégâts
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Vue Liste ou Calendrier */}
        {viewMode === "list" ? (
          <>
            {/* Tableau des véhicules */}
            <TableContainer
              sx={{
                borderRadius: 2,
                overflow: "auto",
                maxWidth: "100%",
                "& .MuiTableCell-root": {
                  whiteSpace: "nowrap",
                  px: { xs: 1, sm: 2 },
                },
                "& .MuiTableCell-head": {
                  backgroundColor: "grey.100",
                  fontWeight: "bold",
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Plaque</TableCell>
                    <TableCell>Véhicule</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="center">Affectations</TableCell>
                    <TableCell align="center">Dernier utilisateur</TableCell>
                    <TableCell align="center">Kilométrage</TableCell>
                    <TableCell align="center">Dégradations</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedVehicles.length > 0 ? (
                    paginatedVehicles.map((vehicle) => (
                      <TableRow key={vehicle._id} hover>
                        <TableCell>{vehicle.plate}</TableCell>
                        <TableCell>
                          {vehicle.brand} {vehicle.model} ({vehicle.year})
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={vehicle.status}
                            color={
                              vehicle.status === "en_service"
                                ? "primary"
                                : vehicle.status === "disponible"
                                ? "success"
                                : vehicle.status === "maintenance"
                                ? "warning"
                                : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<CalendarIcon fontSize="small" />}
                            label={vehicle.assignmentCount || 0}
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {vehicle.lastDriverName ? (
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {vehicle.lastDriverName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {vehicle.lastDriverId}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<SpeedIcon fontSize="small" />}
                            label={`${vehicle.totalKilometers || 0} km`}
                            color="secondary"
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<DamageIcon fontSize="small" />}
                            label={vehicle.totalDamages || 0}
                            color={
                              (vehicle.totalDamages || 0) > 0
                                ? "error"
                                : "default"
                            }
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 0.5,
                            }}
                          >
                            <Tooltip title="Historique dégâts / réparations / entretien">
                              <IconButton
                                component={Link}
                                to={`/vehicules/historique/${vehicle.plate}/degats`}
                                color="secondary"
                                size="small"
                              >
                                <HandymanIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Voir l'historique détaillé">
                              <IconButton
                                component={Link}
                                to={`/vehicules/historique/${vehicle.plate}`}
                                color="primary"
                                size="small"
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Typography color="text.secondary">
                          {search
                            ? "Aucun véhicule ne correspond à votre recherche."
                            : "Aucun véhicule avec historique trouvé."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredAndSortedVehicles.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Véhicules par page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} sur ${count}`
              }
              sx={{ mt: 2, borderTop: "1px solid", borderColor: "divider" }}
            />
          </>
        ) : (
          /* Vue Calendrier - Agenda Google Style */
          <Box
            sx={{
              height: { xs: "70vh", sm: "75vh", md: "80vh" },
              minHeight: { xs: 400, sm: 500, md: 600 },
              width: "100%",
            }}
          >
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: "100%", width: "100%" }}
              date={calendarDate}
              onNavigate={(newDate) => setCalendarDate(newDate)}
              view={calendarView}
              onView={(newView) => setCalendarView(newView)}
              onSelectEvent={(event) => {
                setSelectedEvent(event as CalendarEvent);
                setOpenEventDialog(true);
              }}
              views={["month", "week", "work_week", "day", "agenda"]}
              step={30}
              showMultiDayTimes
              popup
              selectable
              messages={{
                next: "Suivant",
                previous: "Précédent",
                today: "Aujourd'hui",
                month: "Mois",
                week: "Semaine",
                work_week: "Sem. travail",
                day: "Jour",
                agenda: "Agenda",
                date: "Date",
                time: "Heure",
                event: "Affectation",
                allDay: "Toute la journée",
                noEventsInRange: "Aucune affectation dans cette période",
                showMore: (total) => `+ ${total} autre(s)`,
                yesterday: "Hier",
                tomorrow: "Demain",
              }}
              formats={{
                dayFormat: (date, culture, localizer) =>
                  localizer?.format(date, "ddd DD", culture) || "",
                dayHeaderFormat: (date, culture, localizer) =>
                  localizer?.format(date, "dddd DD MMMM", culture) || "",
                dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                  `${localizer?.format(
                    start,
                    "DD MMMM",
                    culture
                  )} - ${localizer?.format(end, "DD MMMM YYYY", culture)}`,
                monthHeaderFormat: (date, culture, localizer) =>
                  localizer?.format(date, "MMMM YYYY", culture) || "",
                agendaDateFormat: (date, culture, localizer) =>
                  localizer?.format(date, "ddd DD MMM", culture) || "",
                agendaTimeFormat: (date, culture, localizer) =>
                  localizer?.format(date, "HH:mm", culture) || "",
                agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
                  `${localizer?.format(
                    start,
                    "HH:mm",
                    culture
                  )} - ${localizer?.format(end, "HH:mm", culture)}`,
                agendaHeaderFormat: ({ start, end }, culture, localizer) =>
                  `${localizer?.format(
                    start,
                    "DD MMMM",
                    culture
                  )} - ${localizer?.format(end, "DD MMMM YYYY", culture)}`,
              }}
              eventPropGetter={(event) => {
                const calEvent = event as CalendarEvent;
                let backgroundColor = "#3174ad";
                let borderColor = "#265985";

                switch (calEvent.resource.status) {
                  case "en_cours":
                    backgroundColor = "#4caf50";
                    borderColor = "#388e3c";
                    break;
                  case "terminée":
                    backgroundColor = "#9e9e9e";
                    borderColor = "#616161";
                    break;
                  case "annulée":
                    backgroundColor = "#f44336";
                    borderColor = "#c62828";
                    break;
                  default:
                    backgroundColor = "#2196f3";
                    borderColor = "#1976d2";
                }

                return {
                  style: {
                    backgroundColor,
                    borderColor,
                    borderLeft: `4px solid ${borderColor}`,
                    borderRadius: "4px",
                    opacity: 0.95,
                    color: "white",
                    border: "none",
                    display: "block",
                    fontSize: "0.85em",
                    padding: "3px 6px",
                    fontWeight: 500,
                    boxShadow:
                      "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
                  },
                };
              }}
              dayPropGetter={(date) => {
                const today = new Date();
                const isToday =
                  date.getDate() === today.getDate() &&
                  date.getMonth() === today.getMonth() &&
                  date.getFullYear() === today.getFullYear();

                if (isToday) {
                  return {
                    style: {
                      backgroundColor: "#e3f2fd",
                    },
                  };
                }
                return {};
              }}
              tooltipAccessor={(event) => {
                const calEvent = event as CalendarEvent;
                return `${calEvent.resource.vehiclePlate} - ${calEvent.resource.driverName}\nStatut: ${calEvent.resource.status}`;
              }}
              components={{
                event: ({ event }) => {
                  const calEvent = event as CalendarEvent;
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {calEvent.resource.vehiclePlate}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7em",
                          lineHeight: 1.1,
                          opacity: 0.9,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {calEvent.resource.driverName}
                      </Typography>
                    </Box>
                  );
                },
                agenda: {
                  event: ({ event }) => {
                    const calEvent = event as CalendarEvent;
                    return (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip
                          label={calEvent.resource.vehiclePlate}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                        <Typography variant="body2">
                          {calEvent.resource.driverName}
                        </Typography>
                        <Chip
                          label={calEvent.resource.status}
                          size="small"
                          color={
                            calEvent.resource.status === "en_cours"
                              ? "success"
                              : calEvent.resource.status === "terminée"
                              ? "default"
                              : calEvent.resource.status === "annulée"
                              ? "error"
                              : "primary"
                          }
                          sx={{ ml: "auto" }}
                        />
                      </Box>
                    );
                  },
                },
              }}
            />

            {/* Dialog détails de l'événement - Tailwind Version */}
            <Dialog
              open={openEventDialog}
              onClose={() => setOpenEventDialog(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                className: "rounded-2xl overflow-hidden shadow-2xl",
                sx: { maxWidth: 700 },
              }}
            >
              {/* Header avec gradient */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <CalendarIcon
                        className="text-white"
                        sx={{ fontSize: 24 }}
                      />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">
                        Détails de l'affectation
                      </h2>
                      <p className="text-blue-100 text-sm">
                        {selectedEvent &&
                          moment(selectedEvent.start).format("DD MMMM YYYY")}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpenEventDialog(false)}
                    className="w-8 h-8 rounded-lg hover:bg-white/20 transition-colors flex items-center justify-center"
                  >
                    <CloseIcon className="text-white" sx={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 bg-gradient-to-br from-gray-50 to-white">
                {selectedEvent && (
                  <div className="space-y-4">
                    {/* Véhicule & Chauffeur - Grid 2 colonnes */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Carte Véhicule */}
                      <div className="bg-white rounded-xl p-5 border-2 border-blue-500 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                            <span className="text-2xl">🚗</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                              Véhicule
                            </p>
                            <h3 className="text-2xl font-bold text-blue-600 truncate">
                              {selectedEvent.resource.vehiclePlate}
                            </h3>
                          </div>
                        </div>
                        {selectedEvent.resource.vehicleBrand && (
                          <div className="mt-3 pt-3 border-t border-blue-100">
                            <p className="text-xs font-semibold text-gray-500 mb-1">
                              Modèle
                            </p>
                            <p className="text-sm font-medium text-gray-800">
                              {selectedEvent.resource.vehicleBrand}{" "}
                              {selectedEvent.resource.vehicleModel}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Carte Chauffeur */}
                      <div className="bg-white rounded-xl p-5 border-2 border-green-500 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                            <span className="text-2xl">👤</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                              Chauffeur
                            </p>
                            <h3 className="text-xl font-bold text-green-600 truncate">
                              {selectedEvent.resource.driverId}
                            </h3>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-green-100">
                          <p className="text-sm font-medium text-gray-800">
                            {selectedEvent.resource.driverName}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Date & Heure */}
                    <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">📅</span>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Date
                            </p>
                          </div>
                          <p className="text-base font-semibold text-gray-800 capitalize">
                            {moment(selectedEvent.start).format(
                              "dddd D MMMM YYYY"
                            )}
                          </p>
                        </div>
                        <div className="h-12 w-px bg-gray-300 hidden md:block"></div>
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">🕐</span>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Horaire
                            </p>
                          </div>
                          <p className="text-base font-semibold text-gray-800">
                            {moment(selectedEvent.start).format("HH:mm")} -{" "}
                            {moment(selectedEvent.end).format("HH:mm")}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Statistiques */}
                    <div className="bg-white rounded-xl p-5 shadow-md border border-gray-200">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">
                        Informations complémentaires
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {/* Km Départ */}
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center transform transition-all hover:scale-105 hover:shadow-md">
                          <SpeedIcon
                            sx={{ fontSize: 32, color: "#3b82f6" }}
                            className="mb-2"
                          />
                          <p className="text-2xl font-bold text-blue-600">
                            {selectedEvent.resource.startMileage
                              ? `${selectedEvent.resource.startMileage.toLocaleString()}`
                              : "N/A"}
                          </p>
                          <p className="text-xs text-gray-600 font-medium mt-1">
                            {selectedEvent.resource.startMileage
                              ? "km départ"
                              : "Km départ"}
                          </p>
                        </div>

                        {/* Km Arrivée */}
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center transform transition-all hover:scale-105 hover:shadow-md">
                          <SpeedIcon
                            sx={{ fontSize: 32, color: "#10b981" }}
                            className="mb-2"
                          />
                          <p className="text-2xl font-bold text-green-600">
                            {selectedEvent.resource.endMileage
                              ? `${selectedEvent.resource.endMileage.toLocaleString()}`
                              : "N/A"}
                          </p>
                          <p className="text-xs text-gray-600 font-medium mt-1">
                            {selectedEvent.resource.endMileage
                              ? "km arrivée"
                              : "Km arrivée"}
                          </p>
                        </div>

                        {/* Dégâts */}
                        <div
                          className={`rounded-lg p-4 text-center transform transition-all hover:scale-105 hover:shadow-md ${
                            (selectedEvent.resource.damageCount || 0) > 0
                              ? "bg-gradient-to-br from-red-50 to-red-100"
                              : "bg-gradient-to-br from-gray-50 to-gray-100"
                          }`}
                        >
                          <DamageIcon
                            sx={{
                              fontSize: 32,
                              color:
                                (selectedEvent.resource.damageCount || 0) > 0
                                  ? "#ef4444"
                                  : "#9ca3af",
                            }}
                            className="mb-2"
                          />
                          <p
                            className={`text-2xl font-bold ${
                              (selectedEvent.resource.damageCount || 0) > 0
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {selectedEvent.resource.damageCount || 0}
                          </p>
                          <p className="text-xs text-gray-600 font-medium mt-1">
                            Dégât
                            {(selectedEvent.resource.damageCount || 0) > 1
                              ? "s"
                              : ""}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Statut */}
                    <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-600">
                        Statut de l'affectation
                      </span>
                      <Chip
                        label={selectedEvent.resource.status}
                        color={
                          selectedEvent.resource.status === "en_cours"
                            ? "success"
                            : selectedEvent.resource.status === "terminée"
                            ? "default"
                            : selectedEvent.resource.status === "annulée"
                            ? "error"
                            : "primary"
                        }
                        className="font-bold"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200">
                <button
                  onClick={() => setOpenEventDialog(false)}
                  className="px-5 py-2.5 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-all duration-200"
                >
                  Fermer
                </button>
                {selectedEvent && (
                  <>
                    <Link
                      to={`/vehicules/historique/${selectedEvent.resource.vehiclePlate}/degats`}
                      className="px-4 py-2.5 rounded-lg border-2 border-blue-600 text-blue-600 font-bold hover:bg-blue-50 transition-all duration-200 flex items-center gap-2"
                    >
                      <HistoryIcon sx={{ fontSize: 20 }} />
                      <span>Historique complet</span>
                    </Link>
                    <Link
                      to={`/vehicules/historique/${selectedEvent.resource.vehiclePlate}/processus/${selectedEvent.resource.assignmentId}`}
                      className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                    >
                      <HistoryIcon sx={{ fontSize: 20 }} />
                      <span>Voir le processus détaillé</span>
                    </Link>
                  </>
                )}
              </div>
            </Dialog>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VehiclesHistoryPage;
